/**
 * Version Service — local/remote version checking, semver comparison
 *
 * Reads local version from build-time constant (__APP_VERSION__).
 * Reads remote version from Supabase app_config table, one row per channel
 * (ADR-004, P4-b). The legacy `version_info` row is FROZEN (ADR-005 amendment b)
 * and is never read for update decisions.
 */

import { supabase } from '../lib/supabase';
import { platform } from '../lib/native';

/**
 * Popup switch (BOARD #42, P4-b): on since 2026-10-25 (Dim). It only bites a
 * channel whose app_config row sets `min`: version_web has min=null, and the
 * store apps (1.10.0) read the frozen legacy row, so nobody sees the dialog
 * until train #1 ships native builds and version_ios / version_android rows
 * are written (min left null at first).
 */
export const UPDATE_CHECK_POPUP_ENABLED = true;

/**
 * Install channel for update checks: 'ios' | 'android' | 'web'.
 * (Play and sideload installs both read the 'android' row. The update action
 * differs: VersionModal asks the native InstallSource plugin who installed the
 * app — Play installs go to the Play listing, others to the APK; see
 * lib/androidUpdateRoute.js.)
 */
export function getChannel() {
  if (platform.isIOS) return 'ios';
  if (platform.isAndroid) return 'android';
  return 'web';
}

/** app_config key holding { latest, min, force_update, release_notes } for a channel. */
export function versionKeyForChannel(channel) {
  return `version_${channel}`;
}

/**
 * Get the locally built-in app version.
 * Set at build time by vite.config.js from package.json.
 */
export function getLocalVersion() {
  // __APP_VERSION__ is defined by Vite at build time
  // eslint-disable-next-line no-undef
  return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
}

/**
 * Fetch remote version info for a channel from Supabase app_config.
 * Returns { latest, min, force_update, release_notes } or null on error.
 * A missing channel row returns null (no check) — never falls back to the
 * frozen legacy `version_info` row.
 */
export async function getRemoteVersionInfo(appName = 'reedle', channel = getChannel()) {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('app_name', appName)
      .eq('key', versionKeyForChannel(channel))
      .single();

    if (error) {
      console.warn('[versionService] Failed to fetch remote version:', error.message);
      return null;
    }

    return data?.value || null;
  } catch (e) {
    console.warn('[versionService] Error fetching remote version:', e);
    return null;
  }
}

/**
 * Compare two semver strings (major.minor.patch).
 * Returns:
 *   -1 if a < b  (a is older)
 *    0 if a === b
 *    1 if a > b  (a is newer)
 */
export function compareVersions(a, b) {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  // Pad to 3 parts
  while (pa.length < 3) pa.push(0);
  while (pb.length < 3) pb.push(0);

  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return 1;
    if (pa[i] < pb[i]) return -1;
  }
  return 0;
}

/**
 * Full update check: compare local vs remote, return status.
 * Returns:
 *   { hasUpdate, isForced, belowMin, channel, key, localVersion, remoteVersion, releaseNotes }
 *   or { hasUpdate: false, error: true, channel, key } on fetch failure
 */
export async function checkForUpdate(appName = 'reedle', channel = getChannel()) {
  const localVersion = getLocalVersion();
  const key = versionKeyForChannel(channel);
  const remote = await getRemoteVersionInfo(appName, channel);

  if (!remote) {
    return {
      hasUpdate: false,
      isForced: false,
      belowMin: false,
      channel,
      key,
      localVersion,
      remoteVersion: null,
      releaseNotes: '',
      error: true,
    };
  }

  const cmp = compareVersions(localVersion, remote.latest);
  const hasUpdate = cmp < 0; // local is older
  const isForced = remote.force_update === true;
  const belowMin = remote.min ? compareVersions(localVersion, remote.min) < 0 : false;

  return {
    hasUpdate,
    isForced: isForced || belowMin,
    belowMin,
    channel,
    key,
    localVersion,
    remoteVersion: remote.latest,
    minVersion: remote.min,
    releaseNotes: remote.release_notes || '',
    error: false,
  };
}
